import React, { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { MechanicService } from '../../services/mechanicService';
import { User, Mail, Phone, MapPin, Save, Edit, Award, TrendingUp } from 'lucide-react';

const MechanicProfile = () => {
  const user = JSON.parse(localStorage.getItem('roadserve_current_user') || '{}');
  const queryClient = useQueryClient();
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [formData, setFormData] = useState({
    name: user?.name || '',
    email: user?.email || '',
    phone: user?.phone || '',
    address: user?.address || '',
  });

  const { data: performanceData } = useQuery({
    queryKey: ['mechanicPerformance', user?.id],
    queryFn: () => MechanicService.getPerformanceStats(user?.id),
    enabled: !!user?.id,
  });
  const performance = performanceData?.data || {};

  const handleSave = async () => {
    if (!formData.name || !formData.phone) {
      alert('Please fill in all required fields');
      return;
    }

    setIsSaving(true);
    try {
      const result = await MechanicService.updateProfile(user?.id, {
        name: formData.name,
        phone: formData.phone,
        address: formData.address,
      });

      if (result.success) {
        // Update local storage
        const updatedUser = { ...user, ...formData };
        localStorage.setItem('roadserve_current_user', JSON.stringify(updatedUser));
        
        // Invalidate queries
        queryClient.invalidateQueries(['mechanicProfile', user?.id]);
        
        alert('Profile updated successfully!');
        setIsEditing(false);
      } else {
        alert('Failed to update profile: ' + result.error);
      }
    } catch (error) {
      alert('Error updating profile: ' + error.message);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Profile</h1>
        <p className="text-gray-600 mt-1">Manage your profile and view performance</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-4">
              <div className="w-20 h-20 bg-orange-100 rounded-full flex items-center justify-center">
                <span className="text-orange-600 font-bold text-2xl">
                  {formData.name?.charAt(0).toUpperCase() || 'M'}
                </span>
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900">{formData.name || 'Mechanic'}</h2>
                <p className="text-gray-500">{formData.email || 'No email'}</p>
              </div>
            </div>
            <button
              onClick={() => isEditing ? handleSave() : setIsEditing(true)}
              disabled={isSaving}
              className="flex items-center space-x-2 px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 disabled:opacity-50"
            >
              {isSaving ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full spinner"></div>
                  <span>Saving...</span>
                </>
              ) : (
                <>
                  {isEditing ? <Save size={18} /> : <Edit size={18} />}
                  <span>{isEditing ? 'Save' : 'Edit'}</span>
                </>
              )}
            </button>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                <User size={16} className="mr-2" />
                Name *
              </label>
              {isEditing ? (
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                  required
                />
              ) : (
                <p className="text-gray-900">{formData.name}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                <Mail size={16} className="mr-2" />
                Email
              </label>
              <p className="text-gray-900">{formData.email}</p>
              <p className="text-xs text-gray-500 mt-1">Email cannot be changed</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                <Phone size={16} className="mr-2" />
                Phone *
              </label>
              {isEditing ? (
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                  required
                />
              ) : (
                <p className="text-gray-900">{formData.phone || 'Not provided'}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                <MapPin size={16} className="mr-2" />
                Address
              </label>
              {isEditing ? (
                <textarea
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  rows={3}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                  placeholder="Enter your address"
                />
              ) : (
                <p className="text-gray-900">{formData.address || 'Not provided'}</p>
              )}
            </div>
          </div>

          {isEditing && (
            <div className="mt-6 flex space-x-3">
              <button
                onClick={() => {
                  setFormData({
                    name: user?.name || '',
                    email: user?.email || '',
                    phone: user?.phone || '',
                    address: user?.address || '',
                  });
                  setIsEditing(false);
                }}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
            </div>
          )}
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-bold text-gray-900 mb-4">Performance</h3>
          <div className="space-y-4">
            <div>
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm text-gray-600">Total Jobs</p>
                <Award size={20} className="text-orange-600" />
              </div>
              <p className="text-2xl font-bold text-gray-900">{performance.totalJobs || performance.completed || 0}</p>
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm text-gray-600">Completed</p>
                <TrendingUp size={20} className="text-green-600" />
              </div>
              <p className="text-2xl font-bold text-gray-900">{performance.completed || 0}</p>
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm text-gray-600">Rating</p>
                <span className="text-xl">⭐</span>
              </div>
              <p className="text-2xl font-bold text-gray-900">
                {performance.rating ? performance.rating.toFixed(1) : performance.averageRating ? performance.averageRating.toFixed(1) : 'N/A'}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MechanicProfile;
